import type { ListBuilderConfiguration } from '../../types';
import { abandonedCartsList } from './lists/abandoned-carts';
import { defaultOrdersAllList } from './lists/default-orders-all';
import { requiresAttentionList } from './lists/requires-attention';
import { requiringApprovalOrdersList } from './lists/requiring-approval-orders';
import { routingList } from './lists/routing';
import { todaysCartsList } from './lists/todays-carts';
import { todaysOrdersList } from './lists/todays-orders';
import { todaysWebCartsList } from './lists/todays-web-carts';

/**
 * Order list configuration
 * Includes multiple filtered views for managing orders and carts
 */
export const ORDER_CONFIG: ListBuilderConfiguration = {
	objectType: 'order',
	lists: [
		todaysOrdersList,
		requiringApprovalOrdersList,
		requiresAttentionList,
		todaysCartsList,
		todaysWebCartsList,
		abandonedCartsList,
		defaultOrdersAllList,
		routingList,
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [20, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'orderKeywords',
			'orderStatus',
			'orderOrigin',
			'orderSalesRep',
			'orderCompany',
			'orderDateRange',
			'orderModifiedDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/orders/:id',
			objectLabel: 'Order',
			objectTable: 'order',
			// Rows on this list are order records keyed by `_id`, so any
			// configured column of the table can be filled in server-side
			// (ListFieldHydrationService).
			rowIdField: '_id',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'externalId2', fieldLabel: 'External ID 2' },
				{ fieldName: 'date', fieldLabel: 'Date' },
				{ fieldName: 'clientName', fieldLabel: 'Client Name' },
				{ fieldName: 'clientAddress1', fieldLabel: 'Client Address 1' },
				{ fieldName: 'clientAddress2', fieldLabel: 'Client Address 2' },
				{ fieldName: 'clientCity', fieldLabel: 'Client City' },
				{ fieldName: 'clientState', fieldLabel: 'Client State' },
				{ fieldName: 'clientZip', fieldLabel: 'Client Zip' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'numberOfLines', fieldLabel: 'Number of Lines' },
				{ fieldName: 'total', fieldLabel: 'Total' },
				{ fieldName: 'customerPO', fieldLabel: 'Customer PO' },
				// Derived field (the order's Operations Instructions note,
				// attached to list rows by OrdersService) — curated here so the
				// column editor's field picker offers it; it has no
				// _object_field row to surface through (PD-13616). The stored
				// fieldName stays `erpComment` so saved list configs keep
				// resolving; only the label follows the PD-14917 taxonomy.
				{ fieldName: 'erpComment', fieldLabel: 'Operations Instructions' },
				// Derived field (PD-13685): units shipped as a percentage of units
				// ordered across the order's lines, attached to list rows by
				// OrdersService when a list's columns request it, and sortable
				// through a sort-only subquery there. Curated so the picker
				// offers it under the same label the Order Details card uses.
				{ fieldName: 'fulfillmentPercent', fieldLabel: 'Fulfillment %' },
			],
		},
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
			],
		},
	],
	tableSorts: [
		{
			id: 'date-desc',
			name: 'Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'date',
					objectTable: 'order',
				},
			],
			description: 'Sort orders by date, newest first',
		},
		{
			id: 'date-asc',
			name: 'Date (Oldest First)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'date',
					objectTable: 'order',
				},
			],
			description: 'Sort orders by date, oldest first',
		},
		{
			id: 'external-id-asc',
			name: 'Order # (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'id',
					objectTable: 'order',
				},
			],
			description: 'Sort orders by order ID, ascending',
		},
		{
			id: 'external-id-desc',
			name: 'Order # (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'id',
					objectTable: 'order',
				},
			],
			description: 'Sort orders by order ID, descending',
		},
		{
			id: 'total-desc',
			name: 'Total (Highest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'total',
					objectTable: 'order',
				},
			],
			description: 'Sort orders by total amount, highest first',
		},
		{
			id: 'status',
			name: 'Status',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'status',
					objectTable: 'order',
				},
			],
			description: 'Sort orders by status',
		},
	],
};
